import React from 'react'
import { useSelector } from 'react-redux'
import { injectIntl, IntlShape, InjectedIntlProps } from 'react-intl'
import { RightCircleFilled } from '@ant-design/icons'
import userService from 'services/user'
import AntdTooltip from 'UI/Tooltip/Tooltip'
import { selectCurrentUser, selectCurrentZone } from 'store/global'
import styles from './Navigation.scss'
import icons from './icons'
import { NAVIGATION_COLLAPSED_WIDTH, NAVIGATION_EXPANDED_WIDTH } from 'containers/App/utils'
import classNames from 'classnames'
import { NavLink } from 'react-router-dom'

type NavigationProps = InjectedIntlProps & {
  intl: IntlShape
  navWidth: number
  setNavWidth: React.Dispatch<React.SetStateAction<number>>
}

interface NavigationLink {
  title: string
  path: string
  iconUrl?: string
  onClick?: () => void
  isDisplayed?: (user: any) => boolean
}

const Navigation: React.FC<NavigationProps> = ({ intl, navWidth, setNavWidth }) => {
  const currentUser = useSelector(selectCurrentUser)

  const currentZone = useSelector(selectCurrentZone)

  const isManagementDisplayed = !userService.isBasicUser(currentUser)

  const links = [
    {
      title: 'Dashboard',
      path: '/dashboard',
      iconUrl: icons.dashboardIconUrl,
    },
    {
      title: 'Businesses',
      path: '/businesses',
      iconUrl: icons.businessesIconUrl,
    },
    currentZone?.airbnbIndices
      ? {
          title: 'Rentals',
          path: '/rentals',
          iconUrl: icons.shortTermRentalsIconUrl,
        }
      : null,
    currentZone?.strIndices && currentZone?.strActivityIndices
      ? {
          title: 'Short-term Rentals',
          path: '/short-term-rentals',
          iconUrl: icons.shortTermRentalsIconUrl,
        }
      : null,
    {
      title: 'Crypto',
      path: '/crypto',
      iconUrl: icons.cryptoIconUrl,
    },
    {
      title: 'Influencers',
      path: '/influencers',
      iconUrl: icons.influencersIconUrl,
    },
    currentZone?.ecommerceIndices && currentZone?.ecommerceActivityIndices
      ? {
          title: 'Ecommerce',
          path: '/ecommerce',
          iconUrl: icons.ecommerceIconUrl,
        }
      : null,
    currentZone?.ecommerceExtendedIndices && currentZone?.ecommerceExtendedEmployeesIndices
      ? {
          title: 'Ecommerce Extended',
          path: '/ecommerce-extended',
          iconUrl: icons.ecommerceExtendedIconUrl,
        }
      : null,
    {
      title: 'Freelancers',
      path: '/freelancers',
      iconUrl: icons.freelancersIconUrl,
    },
    {
      title: 'Offshore',
      path: '/offshore',
      iconUrl: icons.offshoreIconUrl,
    },
    {
      title: 'Data Science',
      path: '/data-science',
      iconUrl: icons.dataScienceIconUrl,
    },
    {
      title: 'Company Overview',
      path: '/company-overview',
      iconUrl: icons.companyOverviewIconUrl,
    },
    {
      title: 'FATCA',
      path: '/fatca',
      iconUrl: icons.fatcaIconUrl,
    },
    {
      title: 'Service Providers',
      path: '/service-providers',
      iconUrl: icons.serviceProviderIconUrl,
    },
    {
      title: 'Management',
      path: '/management',
      iconUrl: icons.managementIconUrl,
      isDisplayed: () => isManagementDisplayed,
    },
  ].filter(Boolean) as NavigationLink[]

  const isExpanded = navWidth === NAVIGATION_EXPANDED_WIDTH

  return (
    <nav className={styles.container} style={{ width: navWidth }}>
      <AntdTooltip
        classNames={{
          root: styles.expandButtonTooltipRoot,
          body: styles.expandButtonTooltipBody,
        }}
        color='#333434'
        placement='right'
        title={isExpanded ? 'Close Navigation' : 'Open Navigation'}>
        <button
          className={styles.expandButton}
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          onClick={e => {
            e.stopPropagation()
            setNavWidth(prev =>
              prev === NAVIGATION_COLLAPSED_WIDTH ? NAVIGATION_EXPANDED_WIDTH : NAVIGATION_COLLAPSED_WIDTH,
            )
          }}
        >
          <RightCircleFilled style={{ color: '#1890FF' }} />
        </button>
      </AntdTooltip>
      <ul className={styles.links}>
        {links
          .filter(({ isDisplayed = () => true }) => isDisplayed(currentUser))
          .map(({ title, path, onClick, iconUrl }, index) => (
            <li
              key={index}
              className={classNames(styles.link, { [styles.selected]: location.pathname.includes(path) })}
            >
              <NavLink to={path} onClick={onClick} className={styles.button}>
                {iconUrl && <img src={iconUrl} alt={title} className={styles.icon} />}
                {isExpanded && <span className={styles.title}>{title}</span>}
              </NavLink>
            </li>
          ))}
      </ul>
    </nav>
  )
}

export default injectIntl(Navigation)
